import { useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Sparkle, ArrowsClockwise, Warning, CheckCircle, GitMerge,
  Envelope, UserCircle, CaretDown, CaretRight,
} from '@phosphor-icons/react';
import { WatchHowLink } from '../components/WatchHowLink';

const MODES = [
  { value: 'email', label: 'By Email (exact match)', icon: Envelope },
  { value: 'name_company', label: 'By Name + Company', icon: UserCircle },
];

export default function MergeDuplicatesPage() {
  const [mode, setMode] = useState('email');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [mergingKey, setMergingKey] = useState(null);
  const [autoMergePreview, setAutoMergePreview] = useState(null);
  const [autoMergeRunning, setAutoMergeRunning] = useState(false);
  const [autoMergeJob, setAutoMergeJob] = useState(null); // {job_id, status, progress_pct, ...}
  const [srcCrmPreview, setSrcCrmPreview] = useState(null);
  const [srcCrmBusy, setSrcCrmBusy] = useState(false);
  const [srcCrmResult, setSrcCrmResult] = useState(null);

  const previewSrcCrmMove = async () => {
    setSrcCrmBusy(true);
    setSrcCrmResult(null);
    try {
      const { data: result } = await api.post('/admin/cleanup/source-crm-to-user2?dry_run=true');
      setSrcCrmPreview(result);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSrcCrmBusy(false);
    }
  };

  const executeSrcCrmMove = async () => {
    if (!window.confirm(
      `Move the stray "source_crm" value into User 2 for ${srcCrmPreview.would_move} contact${srcCrmPreview.would_move === 1 ? '' : 's'}?\n\n` +
      `• The stray field is removed after the move\n` +
      `• Contacts whose User 2 already holds a different value are skipped, never overwritten`
    )) return;
    setSrcCrmBusy(true);
    try {
      const { data: result } = await api.post('/admin/cleanup/source-crm-to-user2?dry_run=false');
      setSrcCrmResult(result);
      setSrcCrmPreview(null);
      toast.success(`Done — ${result.moved} contact${result.moved === 1 ? '' : 's'} now have the CRM name in User 2.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSrcCrmBusy(false);
    }
  };

  const scan = async (newPage = 1, newMode = mode) => {
    setLoading(true);
    setError(null);
    setExpandedGroups({});
    try {
      const { data } = await api.get('/admin/duplicates/scan', {
        params: { mode: newMode, page: newPage, page_size: 20 },
      });
      setData(data);
      setPage(newPage);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (key) => {
    setExpandedGroups((s) => ({ ...s, [key]: !s[key] }));
  };

  const mergeGroup = async (group, primaryId) => {
    const dupIds = group.contacts.map((c) => c.contact_id).filter((id) => id !== primaryId);
    if (dupIds.length === 0) {
      toast.error('Pick a primary that has at least one duplicate');
      return;
    }
    if (!window.confirm(
      `Merge ${dupIds.length} duplicate contact${dupIds.length === 1 ? '' : 's'} into the primary?\n\n` +
      `• Empty fields on the primary will be filled from the duplicates\n` +
      `• Tags will be unioned\n` +
      `• All activities, deals, and relationships will move to the primary\n` +
      `• The ${dupIds.length} duplicate row${dupIds.length === 1 ? '' : 's'} will be permanently deleted`
    )) return;
    setMergingKey(group.key);
    try {
      const { data: result } = await api.post('/admin/duplicates/merge', {
        primary_id: primaryId,
        duplicate_ids: dupIds,
      });
      toast.success(
        `Merged ${result.duplicates_merged} contact${result.duplicates_merged === 1 ? '' : 's'} · ` +
        `${result.activities_reassigned} activities moved · ` +
        `${result.deals_reassigned} deals moved`
      );
      // Re-scan to get the next page of duplicates
      await scan(page, mode);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setMergingKey(null);
    }
  };

  const runAutoMergePreview = async () => {
    setAutoMergeRunning(true);
    try {
      const { data: result } = await api.post('/admin/duplicates/auto-merge-email?dry_run=true');
      setAutoMergePreview(result);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setAutoMergeRunning(false);
    }
  };

  const executeAutoMerge = async () => {
    if (!window.confirm(
      `Auto-merge ALL ${autoMergePreview.would_process_groups} groups of email duplicates? ` +
      `This will permanently delete ${autoMergePreview.would_delete_rows} contact rows. ` +
      `Activities and deals will be reassigned to the most-complete record in each group.`
    )) return;
    setAutoMergeRunning(true);
    try {
      const { data: result } = await api.post('/admin/duplicates/auto-merge-email?dry_run=false');
      toast.success(
        `Done! Processed ${result.groups_processed} groups · ` +
        `Deleted ${result.rows_deleted} duplicate rows · ` +
        `${result.activities_reassigned} activities + ${result.deals_reassigned} deals reassigned`
      );
      setAutoMergePreview(null);
      await scan(1, mode);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setAutoMergeRunning(false);
    }
  };

  const totalPages = data ? Math.ceil(data.total_groups / data.page_size) : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="merge-duplicates-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <GitMerge size={24} weight="bold" className="text-[#0B5394]" />
          Merge Duplicate Contacts
          <WatchHowLink slug="merge-duplicates" className="ml-1" />
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Find and merge duplicate contact records. Activities, deals, and tags are preserved on the kept record.
        </p>
      </div>

      {/* Mode toggle + scan */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Match by</span>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {MODES.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              onClick={() => { setMode(value); scan(1, value); }}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded border transition-colors ${
                mode === value
                  ? 'bg-[#0B5394] text-white border-[#0B5394]'
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
              data-testid={`mode-${value}`}
            >
              <Icon size={14} weight="bold" />
              {label}
            </button>
          ))}
          <button
            onClick={() => scan(1, mode)}
            disabled={loading}
            className="ml-auto flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded bg-slate-100 text-slate-700 border border-slate-300 hover:bg-slate-200 disabled:opacity-50"
            data-testid="scan-btn"
          >
            {loading ? <ArrowsClockwise size={14} className="animate-spin" /> : <Sparkle size={14} weight="bold" />}
            {loading ? 'Scanning…' : (data ? 'Re-scan' : 'Scan')}
          </button>
        </div>

        {/* Auto-merge email duplicates (only relevant for email mode) */}
        {mode === 'email' && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3">
            <div className="text-xs font-bold text-amber-900 mb-1">⚡ Auto-merge ALL email duplicates</div>
            <div className="text-[11px] text-amber-800 mb-2">
              When two contacts share an email, they are almost certainly the same person.
              This one-click action will merge every such group automatically.
            </div>
            {autoMergeJob ? (
              <div className="bg-white border border-amber-300 rounded p-3" data-testid="auto-merge-progress">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold text-slate-800">
                    {autoMergeJob.status === 'running' && '⏳ Merging in progress…'}
                    {autoMergeJob.status === 'complete' && '✅ Merge complete'}
                    {autoMergeJob.status === 'error' && '❌ Merge failed'}
                  </div>
                  <div className="text-[11px] text-slate-600 font-mono">{autoMergeJob.progress_pct?.toFixed(1) || 0}%</div>
                </div>
                <div className="h-2 bg-slate-200 rounded overflow-hidden mb-2">
                  <div
                    className={`h-full transition-all ${autoMergeJob.status === 'error' ? 'bg-red-500' : 'bg-amber-500'}`}
                    style={{ width: `${autoMergeJob.progress_pct || 0}%` }}
                  />
                </div>
                <div className="text-[11px] text-slate-600 grid grid-cols-2 gap-x-3 gap-y-0.5">
                  <span>Groups: <strong>{autoMergeJob.groups_processed}</strong>/{autoMergeJob.total_groups}</span>
                  <span>Rows deleted: <strong>{autoMergeJob.rows_deleted}</strong></span>
                  <span>Activities moved: <strong>{autoMergeJob.activities_reassigned}</strong></span>
                  <span>Deals moved: <strong>{autoMergeJob.deals_reassigned}</strong></span>
                  {autoMergeJob.errors > 0 && <span className="col-span-2 text-red-600">Errors: <strong>{autoMergeJob.errors}</strong></span>}
                  {autoMergeJob.error_message && <span className="col-span-2 text-red-600 break-all">Error: {autoMergeJob.error_message}</span>}
                </div>
                {(autoMergeJob.status === 'complete' || autoMergeJob.status === 'error') && (
                  <button
                    onClick={() => setAutoMergeJob(null)}
                    className="mt-2 px-2 py-1 text-[10px] bg-slate-100 border border-slate-300 rounded hover:bg-slate-200"
                  >
                    Dismiss
                  </button>
                )}
              </div>
            ) : !autoMergePreview ? (
              <button
                onClick={runAutoMergePreview}
                disabled={autoMergeRunning}
                className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs font-semibold rounded"
                data-testid="auto-merge-preview-btn"
              >
                {autoMergeRunning ? 'Calculating…' : 'Preview auto-merge'}
              </button>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-amber-900">
                  Will merge <strong>{autoMergePreview.would_process_groups}</strong> groups,
                  deleting <strong>{autoMergePreview.would_delete_rows}</strong> duplicate rows.
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={executeAutoMerge}
                    disabled={autoMergeRunning}
                    className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-semibold rounded"
                    data-testid="auto-merge-execute-btn"
                  >
                    {autoMergeRunning ? 'Starting…' : 'Yes, merge them all'}
                  </button>
                  <button
                    onClick={() => setAutoMergePreview(null)}
                    className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-xs rounded hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Step 2 — move stray source_crm values into User 2 (Lindy cleanup) */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 mb-6" data-testid="srccrm-cleanup-card">
        <div className="text-sm font-bold text-slate-800 mb-1">
          🧹 Step 2 — Move &ldquo;source_crm&rdquo; → User 2
        </div>
        <div className="text-[11px] text-slate-500 mb-3">
          Fixes contacts that were tagged in a stray <code className="bg-slate-100 px-1 rounded">source_crm</code> field
          instead of User 2. The value moves into User 2 and the stray field is removed. Contacts whose User 2
          already holds a <em>different</em> value are skipped and listed — never overwritten.
          <strong className="text-slate-700"> Run the duplicate auto-merge above first.</strong>
        </div>
        {srcCrmResult ? (
          <div className="bg-green-50 border border-green-200 rounded p-3 text-xs text-green-900" data-testid="srccrm-result">
            ✅ Moved <strong>{srcCrmResult.moved}</strong> · stray keys cleared on{' '}
            <strong>{srcCrmResult.cleared_stray_key_only}</strong> ·{' '}
            {srcCrmResult.conflicts_skipped > 0
              ? <>skipped <strong>{srcCrmResult.conflicts_skipped}</strong> conflict{srcCrmResult.conflicts_skipped === 1 ? '' : 's'} (User 2 already filled)</>
              : 'no conflicts'}
            <button
              onClick={() => setSrcCrmResult(null)}
              className="ml-3 px-2 py-0.5 text-[10px] bg-white border border-green-300 rounded hover:bg-green-100"
            >
              Dismiss
            </button>
          </div>
        ) : !srcCrmPreview ? (
          <button
            onClick={previewSrcCrmMove}
            disabled={srcCrmBusy}
            className="px-3 py-1.5 bg-[#0B5394] hover:bg-[#094180] disabled:opacity-50 text-white text-xs font-semibold rounded"
            data-testid="srccrm-preview-btn"
          >
            {srcCrmBusy ? 'Scanning…' : 'Preview (dry run)'}
          </button>
        ) : (
          <div className="space-y-2" data-testid="srccrm-preview">
            {srcCrmPreview.scanned === 0 ? (
              <div className="text-xs text-green-700 flex items-center gap-1.5">
                <CheckCircle size={14} weight="fill" /> Nothing to clean — no stray source_crm fields found. 🎉
              </div>
            ) : (
              <>
                <div className="text-xs text-slate-800">
                  Will move <strong>{srcCrmPreview.would_move}</strong> value{srcCrmPreview.would_move === 1 ? '' : 's'} into User 2
                  {srcCrmPreview.cleared_stray_key_only > 0 && <> · clear {srcCrmPreview.cleared_stray_key_only} empty stray key{srcCrmPreview.cleared_stray_key_only === 1 ? '' : 's'}</>}
                  {srcCrmPreview.conflicts_skipped > 0 && <> · <span className="text-amber-700 font-semibold">skip {srcCrmPreview.conflicts_skipped} conflict{srcCrmPreview.conflicts_skipped === 1 ? '' : 's'}</span></>}
                </div>
                {Object.keys(srcCrmPreview.value_breakdown || {}).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(srcCrmPreview.value_breakdown).map(([v, n]) => (
                      <span key={v} className="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-mono text-slate-700">
                        {v}: <strong>{n}</strong>
                      </span>
                    ))}
                  </div>
                )}
                {(srcCrmPreview.conflicts_sample || []).length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[10px] text-amber-900 max-h-32 overflow-y-auto">
                    <div className="font-bold mb-1">Skipped (User 2 already filled with something else):</div>
                    {srcCrmPreview.conflicts_sample.map((c) => (
                      <div key={c.contact_id}>
                        {c.name} {c.company && `(${c.company})`} — User 2: &ldquo;{c.existing_user2}&rdquo; vs incoming &ldquo;{c.incoming_value}&rdquo;
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            <div className="flex gap-2">
              {srcCrmPreview.scanned > 0 && srcCrmPreview.would_move > 0 && (
                <button
                  onClick={executeSrcCrmMove}
                  disabled={srcCrmBusy}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-semibold rounded"
                  data-testid="srccrm-execute-btn"
                >
                  {srcCrmBusy ? 'Moving…' : 'Move them now'}
                </button>
              )}
              <button
                onClick={() => setSrcCrmPreview(null)}
                className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-xs rounded hover:bg-slate-50"
              >
                {srcCrmPreview.scanned === 0 ? 'Close' : 'Cancel'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded text-sm flex items-center gap-2">
          <Warning size={16} weight="fill" />
          {error}
        </div>
      )}

      {data && !error && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="text-sm text-slate-700">
              {data.total_groups === 0 ? (
                <span className="flex items-center gap-1.5 text-green-700">
                  <CheckCircle size={16} weight="fill" />
                  No duplicates found in this mode 🎉
                </span>
              ) : (
                <>
                  <strong>{data.total_groups.toLocaleString()}</strong> duplicate groups · showing page {page} of {totalPages || 1}
                </>
              )}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => scan(page - 1, mode)}
                  disabled={page <= 1 || loading}
                  className="px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
                >
                  Prev
                </button>
                <button
                  onClick={() => scan(page + 1, mode)}
                  disabled={page >= totalPages || loading}
                  className="px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          <div className="divide-y divide-slate-100">
            {data.groups.map((group) => {
              const isExpanded = !!expandedGroups[group.key];
              const isMerging = mergingKey === group.key;
              return (
                <div key={group.key} className="p-4" data-testid={`dup-group-${group.key.replace(/[^a-z0-9]/gi, '-')}`}>
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => toggleGroup(group.key)}
                    data-testid="dup-group-row"
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
                      <span className="font-mono text-sm text-slate-800 truncate max-w-[500px]">{group.key}</span>
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded">
                        ×{group.count}
                      </span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-3 ml-5 space-y-2">
                      <ContactGroupTable
                        contacts={group.contacts}
                        suggestedPrimaryId={group.suggested_primary_id}
                        onMerge={(primaryId) => mergeGroup(group, primaryId)}
                        merging={isMerging}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!data && !loading && !error && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 text-center text-slate-500 text-sm">
          Click <strong>Scan</strong> to find duplicate contacts.
        </div>
      )}
    </div>
  );
}

function ContactGroupTable({ contacts, suggestedPrimaryId, onMerge, merging }) {
  const [primaryId, setPrimaryId] = useState(suggestedPrimaryId);

  return (
    <div>
      <div className="overflow-x-auto border border-slate-200 rounded">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-2 py-2 text-left font-semibold text-slate-600">Primary?</th>
              <th className="px-2 py-2 text-left font-semibold text-slate-600">Name</th>
              <th className="px-2 py-2 text-left font-semibold text-slate-600">Company</th>
              <th className="px-2 py-2 text-left font-semibold text-slate-600">Email</th>
              <th className="px-2 py-2 text-left font-semibold text-slate-600">Phone</th>
              <th className="px-2 py-2 text-left font-semibold text-slate-600">Status</th>
              <th className="px-2 py-2 text-left font-semibold text-slate-600">Tags</th>
              <th className="px-2 py-2 text-left font-semibold text-slate-600">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {contacts.map((c) => (
              <tr key={c.contact_id} className={primaryId === c.contact_id ? 'bg-blue-50' : ''}>
                <td className="px-2 py-2">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="radio"
                      name={`primary-${contacts[0]?.contact_id || 'g'}`}
                      checked={primaryId === c.contact_id}
                      onChange={() => setPrimaryId(c.contact_id)}
                      className="w-3 h-3 accent-[#0B5394]"
                    />
                    {c.contact_id === suggestedPrimaryId && (
                      <span className="text-[9px] text-blue-700 font-bold">SUGGESTED</span>
                    )}
                  </label>
                </td>
                <td className="px-2 py-2 text-slate-800">
                  {c.first_name} {c.last_name}
                </td>
                <td className="px-2 py-2 text-slate-700">{c.company || <span className="text-slate-400">—</span>}</td>
                <td className="px-2 py-2 text-slate-700 font-mono">{c.email || <span className="text-slate-400">—</span>}</td>
                <td className="px-2 py-2 text-slate-700">{c.phone || <span className="text-slate-400">—</span>}</td>
                <td className="px-2 py-2 text-slate-700">{c.status || <span className="text-slate-400">—</span>}</td>
                <td className="px-2 py-2 text-slate-600">{(c.tags || []).join(', ') || <span className="text-slate-400">—</span>}</td>
                <td className="px-2 py-2 text-slate-500 text-[10px]">{(c.created_at || '').slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex justify-end">
        <button
          onClick={() => onMerge(primaryId)}
          disabled={merging || !primaryId}
          className="px-3 py-1.5 bg-[#0B5394] hover:bg-[#094180] disabled:opacity-50 text-white text-xs font-semibold rounded flex items-center gap-1.5"
          data-testid="merge-group-btn"
        >
          {merging ? <ArrowsClockwise size={12} className="animate-spin" /> : <GitMerge size={12} weight="bold" />}
          {merging ? 'Merging…' : `Merge ${contacts.length - 1} into primary`}
        </button>
      </div>
    </div>
  );
}
